/**
 * Giacenze e spedizioni verso i negozi (fase 4 del motore): preparazione delle giacenze (sede, zone,
 * conta) e spedizioni di un ordine del negozio registrate in Flusia. Tutto passa dal core
 * (api/stockSetupRoutes.ts, api/shipmentRoutes.ts); `available` = il motore e' in fase 4.
 */
import { call, newIdempotencyKey, type ApiResult } from './api';

export interface StockSetup {
  available: boolean;
  provider: 'shopify' | 'woocommerce';
  owner: 'flusia' | 'store' | null;
  /**
   * linked_items: articoli collegati (su `items_sampled`) attivi nella sede; null = lettura non riuscita;
   * assente con un core precedente.
   */
  locations: { id: string; name: string; active: boolean; fulfills_online: boolean; linked_items?: number | null }[];
  items_sampled?: number;
  zones: { id: string; name: string; code?: string | null }[];
  location_id: string | null;
  zone_ids: string[];
  /** Sedi rifornite con le loro zone (la predefinita per prima). */
  mapping: { location_id: string; zone_ids: string[] }[];
  counted_at: string | null;
  activate_missing: boolean;
  unload_on_store_fulfillment: boolean;
  safety_stock: number;
  min_shelf_life_days: number;
  new_products_from_store: boolean;
  /** Disponibilita' senza quantita' (settings.stock.availability_only); assente con un core precedente. */
  availability_only?: boolean;
  not_activated: number;
  products: { linked: number; managed: number; zero_in_flusia: number };
}

export interface StockOptions {
  activate_missing: boolean;
  unload_on_store_fulfillment: boolean;
  safety_stock?: number;
  min_shelf_life_days?: number;
  new_products_from_store?: boolean;
  availability_only?: boolean;
}

export interface OrderShipments {
  available: boolean;
  order: { id: string; status: string; external_number: string | null };
  lines: { order_item_id: string; name: string; sku: string | null; quantity: number; open: number }[];
  shipments: { id: string; status: string; carrier: string | null; tracking_numbers: string[]; created_at: string; last_error: string | null; lines: number }[];
  carriers: string[];
}

export interface NewOrderShipment {
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  without_tracking: boolean;
  notify_customer: boolean;
}

const id = (v: string) => encodeURIComponent(v);

/** Per useCommerceQuery: { data, error }. */
const asQuery = <T,>(r: ApiResult<T>): { data: T; error: null } | { data: null; error: string } =>
  r.ok ? { data: r.data, error: null } : { data: null, error: r.message || r.code };

export const stockApi = {
  getSetup: async (storeId: string) => asQuery(await call<StockSetup>(`commerce/stores/${id(storeId)}/stock/setup`, 'GET')),
  saveSetup: (storeId: string, b: { location_id: string | null; zone_ids: string[]; counted: true; locations?: { location_id: string; zone_ids: string[] }[] }) =>
    call<{ ok: true }>(`commerce/stores/${id(storeId)}/stock/setup`, 'POST', b),
  saveOptions: (storeId: string, b: StockOptions) =>
    call<{ ok: true }>(`commerce/stores/${id(storeId)}/stock/options`, 'POST', { ...b }),
  getShipments: async (orderId: string) => asQuery(await call<OrderShipments>(`commerce/orders/${id(orderId)}/shipments`, 'GET')),
  ship: (orderId: string, b: NewOrderShipment) =>
    call<{ ok: true; shipment_id: string; queued: boolean }>(`commerce/orders/${id(orderId)}/shipments`, 'POST', { ...b }, newIdempotencyKey()),
};

/** Stato della spedizione in parole. */
export const SHIPMENT_STATUS_TEXT: Readonly<Record<string, string>> = Object.freeze({
  draft: 'Da completare',
  ready: 'In invio al negozio',
  sent: 'Comunicata al negozio',
  delivered: 'Consegnata',
  error: 'Non comunicata al negozio',
  blocked: 'Bloccata',
  cancelled: 'Annullata',
});
